/**
 * GIWAHS — RFQ Status Lifecycle
 * ---------------------------------------------------------------------
 * Source-of-truth for the 8-state RFQ workflow. Single ordered list +
 * per-state metadata. Used by:
 *   • /pages/RfqTracking.jsx  — buyer-facing operational dashboard
 *   • /lib/dataClient.js      — mock progression + admin updates
 *   • /supabase/schema.sql    — keep enum in sync when going live
 *
 * Adding a new state: append to RFQ_STATUS_ORDER, add an entry to
 * RFQ_STATUS_META, update the enum in schema.sql, and ship.
 */

export const RFQ_STATUS_ORDER = [
  'submitted',
  'in_queue',
  'reviewing_requirements',
  'supplier_shortlisting',
  'quotes_requested',
  'quotes_received',
  'buyer_review',
  'closed',
];

export const RFQ_STATUS_META = {
  submitted: {
    label: 'Submitted',
    short: 'Submitted',
    description: 'Your request has been logged with the GIWAHS sourcing desk.',
    estimate: 'Just now',
  },
  in_queue: {
    label: 'In Queue',
    short: 'In queue',
    description: "We've placed your request in the active sourcing queue. A sourcing lead will pick it up shortly.",
    estimate: 'Within 2 business hours',
  },
  reviewing_requirements: {
    label: 'Reviewing Requirements',
    short: 'Reviewing',
    description: 'A sourcing lead is reading your brief, normalising specifications, and flagging any clarification needs.',
    estimate: 'Within 1 business day',
  },
  supplier_shortlisting: {
    label: 'Supplier Shortlisting',
    short: 'Shortlisting',
    description: 'We are matching your brief against verified factories — filtering by category, geography, capacity, and trust score.',
    estimate: 'Within 2 business days',
  },
  quotes_requested: {
    label: 'Quotes Requested',
    short: 'Awaiting quotes',
    description: 'Shortlisted factories have received your brief. They typically respond within 2 to 4 business days.',
    estimate: 'Within 4 business days',
  },
  quotes_received: {
    label: 'Quotes Received',
    short: 'Quotes received',
    description: 'Factory quotes have started landing. We aggregate, normalise, and benchmark them before sharing.',
    estimate: 'Within 1 business day',
  },
  buyer_review: {
    label: 'Buyer Review',
    short: 'Your turn',
    description: 'A curated comparison is ready for your review. Reply to the email or use the platform to proceed.',
    estimate: 'No timer — your call',
  },
  closed: {
    label: 'Closed',
    short: 'Closed',
    description: 'This RFQ has been closed. Either a supplier was selected, the brief was retracted, or it expired without a match.',
    estimate: '—',
  },
};

export const indexOfStatus = (s) => RFQ_STATUS_ORDER.indexOf(s);
export const isTerminal = (s) => s === 'closed';
export const nextStatus = (s) => {
  const i = indexOfStatus(s);
  if (i < 0 || i >= RFQ_STATUS_ORDER.length - 1) return null;
  return RFQ_STATUS_ORDER[i + 1];
};
